import React, { forwardRef } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Lock } from 'lucide-react';
import { GridColumn, SortState, SortDirection } from '../../../types/datagrid';

interface GridHeaderProps {
  columns: GridColumn[];
  sorting: SortState[];
  onSort?: (columnId: string) => void;
  onMultiSort?: (columnId: string) => void;
  onColumnResize?: (columnId: string, width: number) => void;
  onColumnReorder?: (columnId: string, targetIndex: number) => void;
  height?: number;
  sticky?: boolean;
  compactDensity?: boolean;
  permissions?: {
    canEdit?: boolean;
  };
}

export const GridHeader = forwardRef<HTMLDivElement, GridHeaderProps>(
  (
    {
      columns,
      sorting,
      onSort,
      onMultiSort,
      onColumnResize,
      height = 48,
      sticky = true,
      compactDensity = true,
    },
    ref
  ) => {
    const getSortState = (columnId: string) => {
      const sort = sorting.find(s => s.field === columnId);
      return {
        direction: sort?.direction || null,
        order: sort?.order || 0,
        isActive: !!sort,
        isMultiSort: sorting.length > 1,
      };
    };

    const handleSort = (column: GridColumn, event: React.MouseEvent) => {
      if (!column.isSortable) return;

      if (event.shiftKey && onMultiSort) {
        onMultiSort(column.id);
      } else if (onSort) {
        onSort(column.id);
      }
    };

    const getSortIcon = (direction: SortDirection, isActive: boolean) => {
      if (!isActive) {
        return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
      }

      return direction === 'asc' ? (
        <ChevronUp className="h-4 w-4 text-brand-600" />
      ) : (
        <ChevronDown className="h-4 w-4 text-brand-600" />
      );
    };

    return (
      <div
        ref={ref}
        className={`bg-gray-50 border-b border-gray-200 ${sticky ? 'sticky top-0 z-20' : ''}`}
        style={{ height }}
        role="row"
      >
        <div className="flex h-full">
          {columns.map(column => {
            const sortState = getSortState(column.id);
            const canSort = column.isSortable && (onSort || onMultiSort);

            return (
              <div
                key={column.id}
                className={`
                flex items-center px-3 border-r border-gray-200 last:border-r-0
                ${canSort ? 'cursor-pointer hover:bg-gray-100' : ''}
                ${sortState.isActive ? 'bg-brand-50' : ''}
                ${column.isPinned ? 'bg-gray-100' : ''}
              `}
                style={{
                  width: column.width || 150,
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth,
                }}
                role="columnheader"
                aria-sort={
                  sortState.isActive
                    ? sortState.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
                aria-label={`${column.label}${sortState.isActive ? `, sorted ${sortState.direction}ending` : ', not sorted'}`}
                onClick={e => handleSort(column, e)}
                onKeyDown={e => {
                  if ((e.key === 'Enter' || e.key === ' ') && canSort) {
                    e.preventDefault();
                    handleSort(column, e as any);
                  }
                }}
                tabIndex={canSort ? 0 : -1}
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {/* Column Label */}
                  <span
                    className={`
                  flex-1 text-left font-medium text-gray-900 truncate
                  ${compactDensity ? 'text-xs' : 'text-sm'}
                `}
                  >
                    {column.label}
                  </span>

                  {/* Private/Locked Indicator */}
                  {column.isPrivate && (
                    <span title="Private column">
                      <Lock className="h-3 w-3 text-gray-400" aria-label="Private column" />
                    </span>
                  )}

                  {/* Calculated Column Indicator */}
                  {column.type === 'calculated' && (
                    <span
                      className="text-xs text-blue-600 font-mono"
                      title={`Formula: ${column.calculated?.formula || 'Unknown'}`}
                    >
                      fx
                    </span>
                  )}

                  {/* Sort Indicator */}
                  {canSort && (
                    <div className="flex items-center space-x-1">
                      {getSortIcon(sortState.direction, sortState.isActive)}
                      {sortState.isMultiSort && sortState.order > 0 && (
                        <span className="text-xs text-brand-600 font-medium">
                          {sortState.order}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Resize Handle */}
                {column.isResizable && onColumnResize && (
                  <div
                    className="w-1 h-full bg-transparent hover:bg-brand-500 cursor-col-resize ml-2"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      const startX = e.clientX;
                      const startWidth = column.width || 150;

                      const handleMouseMove = (e: MouseEvent) => {
                        const diff = e.clientX - startX;
                        const newWidth = Math.max(column.minWidth, startWidth + diff);
                        onColumnResize(column.id, newWidth);
                      };

                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };

                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    onDoubleClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Auto-size column - would need data to calculate properly
                      onColumnResize(column.id, column.minWidth);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
